import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { checkAuthStatus } from "./actions/auth";
import { loadUser } from "./actions/user";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { showNotification } from "./actions/notification";

import LandingPage from "./componentes/LandingPage";
import CadastraUsuario from "./componentes/CadastraUsuario";
import LoginPage from "./componentes/LoginPage";
import Dashboard from "./componentes/Dashboard";
import ProtectedRoute from "./componentes/ProtectedRoute";
import Spa from "./componentes/Spa";
import EditUser from "./componentes/EditUser.js";
import Catalogo from "./componentes/Catalogo/index.js";
import Planos from "./componentes/Planos/index.js";
import Termos from "./componentes/Termos/index.js";
import Politicas from "./componentes/Politicas/index.js";
import EditLoja from "./componentes/EditLoja/index.js";
import Produtos from "./componentes/Produtos/index.js";
import Fotos from "./componentes/Fotos/index.js";
import Tags from "./componentes/Tags/index.js";
import Home from "./componentes/Home/index.js";
import GlobalAlert from "./componentes/GlobalAlert";
import Ajuda from "./componentes/Ajuda/index.js";
import ContaAtivada from "./componentes/ContaAtivada";
import OnboardingWizard from "./componentes/OnboardingWizard";
import Notificacoes from "./componentes/Notificacoes";

import NProgress from "nprogress";
import "nprogress/nprogress.css";
import CookieConsent from "react-cookie-consent";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import GerarLink from "./componentes/GerarLink.js/index.js";
import {
  ACTIVATION_REDIRECT_STORAGE_KEY,
  hasPlanAccess,
  hasStripeCustomer,
  isPaymentFailureIssue,
} from "./utils/subscription";
import {
  COOKIE_CONSENT_NAME,
  acceptAllPrivacyPreferences,
  declineOptionalPrivacyPreferences,
} from "./utils/privacyConsent";

NProgress.configure({ showSpinner: false, trickle: false });
dayjs.locale("pt-br");

function App() {
  const dispatch = useDispatch();
  const { isAuthenticated, loading: authLoading } = useSelector((state) => state.auth);
  const { loading: userLoading, profile } = useSelector((state) => state.user);
  const isAppLoading = authLoading || userLoading;
  const location = useLocation();
  const navigate = useNavigate();

  const onboardingAlertShownRef = useRef(false);
  const expiredAlertShownRef = useRef(false);

  useEffect(() => {
    dispatch(checkAuthStatus());
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(loadUser());
    }
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    if (isAppLoading) {
      NProgress.start();
    } else {
      NProgress.done();
    }
  }, [isAppLoading]);

  useEffect(() => {
    if (!isAuthenticated || !location.pathname.startsWith("/app")) return;
    if (!profile) return;

    const hasStripe = hasStripeCustomer(profile);
    const hasAccess = hasPlanAccess(profile);
    const activationRedirectPending =
      window.sessionStorage.getItem(ACTIVATION_REDIRECT_STORAGE_KEY) === "1";

    if (hasAccess && activationRedirectPending && location.pathname !== "/app/conta-ativada") {
      window.sessionStorage.removeItem(ACTIVATION_REDIRECT_STORAGE_KEY);
      navigate("/app/conta-ativada", { replace: true });
      return;
    }

    if (hasAccess) return;

    // Usuario com historico no Stripe: orientar reativacao pelo portal/planos.
    if (hasStripe && !expiredAlertShownRef.current) {
      expiredAlertShownRef.current = true;
      const pagamentoFalhou = isPaymentFailureIssue(profile);
      dispatch(
        showNotification({
          title: pagamentoFalhou ? "Pagamento não aprovado" : "Plano inativo",
          message: pagamentoFalhou
            ? "Não foi possível processar o último pagamento. Atualize o método de pagamento em /app/planos."
            : "Reative seu plano para continuar editando.",
          color: "warning",
          duration: 9000,
          actionLabel: "Reativar Plano",
          actionTo: "/app/planos",
        })
      );
      return;
    }

    // Usuario sem plano carregado: orientar escolha do plano grátis ou upgrade.
    if (!hasStripe && !onboardingAlertShownRef.current) {
      onboardingAlertShownRef.current = true;
      dispatch(
        showNotification({
          title: "Comece gratuitamente",
          message: "Crie sua vitrine grátis ou escolha um plano pago quando precisar de mais capacidade.",
          color: "info",
          duration: 10000,
          actionLabel: "Ver Planos",
          actionTo: "/app/planos",
        })
      );
    }
  }, [dispatch, isAuthenticated, location.pathname, navigate, profile]);

  if (isAppLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <h1 className="text-lg font-bold text-gray-800">Carregando...</h1>
      </div>
    );
  }

  return (
    <>
      <GlobalAlert />
      <CookieConsent
        location="bottom"
        buttonText="Aceitar todos"
        declineButtonText="Recusar"
        enableDeclineButton
        cookieName={COOKIE_CONSENT_NAME}
        onAccept={acceptAllPrivacyPreferences}
        onDecline={declineOptionalPrivacyPreferences}
        style={{
          background: "#f1f5f9",
          color: "#1e293b",
          fontSize: "0.875rem",
        }}
        buttonStyle={{
          backgroundColor: "#2563eb",
          color: "#ffffff",
          fontSize: "0.75rem",
          padding: "6px 14px",
          borderRadius: "9999px",
          margin: "4px",
        }}
        declineButtonStyle={{
          backgroundColor: "#e2e8f0",
          color: "#1e293b",
          fontSize: "0.75rem",
          padding: "6px 14px",
          borderRadius: "9999px",
          margin: "4px",
        }}
        expires={150}
      >
        A Vintree usa cookies e tecnologias semelhantes para medir visitas,
        cliques e buscas no catálogo, além de permitir recursos de marketing
        quando autorizados. Você pode gerenciar suas preferências pelo ícone de
        privacidade no catálogo.
      </CookieConsent>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/:username" element={<Catalogo />} />
        <Route path="/cadastrar" element={<CadastraUsuario />} />
        <Route path="/entrar" element={<LoginPage />} />
        <Route path="/termos" element={<Termos />} />
        <Route path="/politicas" element={<Politicas />} />
        <Route
          path="/app/onboarding"
          element={
            <ProtectedRoute>
              <OnboardingWizard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/*"
          element={
            <ProtectedRoute>
              <Spa />
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="planos" element={<Planos />} />
          <Route path="user" element={<EditUser />} />
          <Route path="loja" element={<EditLoja />} />
          <Route path="produtos" element={<Produtos />} />
          <Route path="fotos" element={<Fotos />} />
          <Route path="tags" element={<Tags />} />
          <Route path="link" element={<GerarLink />} />
          <Route path="home" element={<Home />} />
          <Route path="notificacoes" element={<Notificacoes />} />
          <Route path="ajuda" element={<Ajuda />} />
          <Route path="conta-ativada" element={<ContaAtivada />} />
        </Route>
      </Routes>
    </>
  );
}

export default App;
